import { useState, type ReactNode } from 'react';
import {
  ChartReadout,
  ChartTip,
  useActiveMark,
  useMeasuredWidth,
  type TipContent,
} from '@paige/ui';
import { periodLabel, type Period } from './period';
import {
  indexAt,
  midOf,
  nothingToDraw,
  plotOf,
  type ChartSeries,
  type Plot,
} from './period-chart-geometry';
import { BucketLabels, PlotAxes, PlotMarks } from './PeriodChartMarks';
import { PeriodChartTable } from './PeriodChartTable';
import './period-chart.css';

export interface PeriodChartProps {
  /** Spoken name of the picture: "Pages read by day, Oct 1 to Oct 7". */
  label: string;
  buckets: readonly Period[];
  series: readonly ChartSeries[];
  /** The period before, cut into as many buckets: a faint dashed line against the FIRST series. */
  previous?: { label: string; values: readonly number[] } | undefined;
  /** A click, Enter or Space on a bucket. */
  onPick?: ((bucket: Period) => void) | undefined;
  picked?: Period | null | undefined;
  height?: number | undefined;
  /** What to say when every value is zero. */
  empty?: string | undefined;
  /** More lines for a bucket's tooltip, after the series (a margin worked out from two of them). */
  tipLines?: ((index: number) => readonly string[]) | undefined;
}

/** What a bucket's tooltip says: its days, then every series' value in the series' own format. */
function tipOf(props: PeriodChartProps, i: number): TipContent | null {
  const bucket = props.buckets[i];
  const first = props.series[0];
  if (!bucket || !first) return null;
  const lines = props.series.map((s) => `${s.label}: ${s.format(s.values[i] ?? 0)}`);
  if (props.previous)
    lines.push(`${props.previous.label}: ${first.format(props.previous.values[i] ?? 0)}`);
  return {
    title: periodLabel(bucket),
    lines: [...lines, ...(props.tipLines?.(i) ?? [])],
    hint: props.onPick ? 'show these days only' : undefined,
  };
}

/** The key under the chart: a swatch a series, and the previous period's dashes. */
function Legend({ series, previous }: Pick<PeriodChartProps, 'series' | 'previous'>): ReactNode {
  if (series.length < 2 && !previous) return null;
  return (
    <ul className="ad-pchart__legend">
      {series.map((s, i) => (
        <li key={s.key}>
          <span
            className={`ad-pchart__swatch ad-pchart__swatch--${s.kind} ad-pchart__s--${s.tone ?? (i === 0 ? 'accent' : 'muted')}`}
            aria-hidden="true"
          />
          {s.label}
        </li>
      ))}
      {previous ? (
        <li>
          <span className="ad-pchart__swatch ad-pchart__swatch--previous" aria-hidden="true" />
          {previous.label}
        </li>
      ) : null}
    </ul>
  );
}

/** The pointer's bucket: the x in the plot's own pixels, whatever size the SVG is shown at. */
function bucketAt(plot: Plot, e: { clientX: number; currentTarget: SVGSVGElement }): number {
  const box = e.currentTarget.getBoundingClientRect();
  const x = box.width > 0 ? ((e.clientX - box.left) / box.width) * plot.width : 0;
  return indexAt(plot, x);
}

/** The active bucket's tooltip over the plot on a desk; a phone reads it in the strip above instead. */
function Tip({ plot, tip, at }: { plot: Plot; tip: TipContent | null; at: number | null }) {
  if (!tip || at === null || plot.narrow) return null;
  return <ChartTip tip={tip} x={midOf(plot, at)} y={plot.pad.top} width={plot.width} />;
}

/** The plot itself: one tab stop; the arrows move between buckets, Enter picks, hover reads. */
function Plotted({ props, plot }: { props: PeriodChartProps; plot: Plot }): ReactNode {
  const { buckets, series, onPick } = props;
  const pick = (i: number) => {
    const bucket = buckets[i];
    if (bucket && onPick) onPick(bucket);
  };
  const mark = useActiveMark(buckets.length, { onPick: onPick ? pick : undefined });
  const tip = mark.active === null ? null : tipOf(props, mark.active);
  const picked = buckets.findIndex((b) => b.from === props.picked?.from);
  return (
    <>
      {plot.narrow ? <ChartReadout tip={tip} empty="Touch a bar to read it." /> : null}
      <svg
        className={`pui-chart pui-chart--focusable${onPick ? ' ad-pchart--pickable' : ''}`}
        width={plot.width}
        height={plot.height}
        viewBox={`0 0 ${plot.width} ${plot.height}`}
        role="img"
        aria-label={`${props.label}. The arrow keys read each bucket.`}
        {...mark.focusProps}
        onPointerMove={(e) => mark.setActive(bucketAt(plot, e))}
        onPointerLeave={() => mark.setActive(null)}
        onClick={(e) => pick(bucketAt(plot, e))}
      >
        <PlotAxes plot={plot} series={series} />
        <PlotMarks
          plot={plot}
          series={series}
          previous={props.previous?.values}
          active={mark.active}
          picked={picked >= 0 ? picked : null}
        />
        <BucketLabels plot={plot} buckets={buckets} />
      </svg>
      <Tip plot={plot} tip={tip} at={mark.active} />
    </>
  );
}

/**
 * The one chart for a bucketed period (This week, Revenue, Customers): bars and lines over the
 * period's buckets, each axis one scale, the period before as a faint dashed line, a tooltip on hover
 * or the arrow keys, a pick on click or Enter, and the same numbers as a table behind a small button.
 * When nothing happened it says so in words and draws no axis.
 */
export function PeriodChart(props: PeriodChartProps): ReactNode {
  const { buckets, series, height = 220 } = props;
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [table, setTable] = useState(false);
  // The frame is measured from the first render, so a period that fills in later draws at the real width.
  if (nothingToDraw(series, buckets.length)) {
    return (
      <div className="ad-pchart pui-chart-frame" ref={ref}>
        <p className="ad-pchart__empty">{props.empty ?? 'Nothing in this period.'}</p>
      </div>
    );
  }
  const previous = props.previous?.values;
  const plot = plotOf({ width, height, count: buckets.length, series, previous });
  return (
    <div className="ad-pchart pui-chart-frame" ref={ref}>
      <div className="pui-chart-frame">
        <Plotted props={props} plot={plot} />
      </div>
      <div className="ad-pchart__foot">
        <Legend series={series} previous={props.previous} />
        <button
          type="button"
          className="ad-pchart__toggle"
          aria-expanded={table}
          onClick={() => setTable((t) => !t)}
        >
          {table ? 'Hide table' : 'Show as table'}
        </button>
      </div>
      <PeriodChartTable
        caption={props.label}
        buckets={buckets}
        series={series}
        previous={props.previous}
        shown={table}
      />
    </div>
  );
}
